import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const COLORS = {
  ink: "#1a2330",
  inkMuted: "#6b7280",
  dark: "#0e2030",
  card: "#ffffff",
  bgAlt: "#ede5d6",
  border: "rgba(15, 30, 45, 0.08)",
  borderStrong: "rgba(15, 30, 45, 0.16)",
};
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";

export const PAGE_SIZE = 20;

// Simple prev/next + numbered pager for admin list views. `total` is the
// full (post-filter) item count; `page` is 1-indexed.
export default function Pagination({ page, total, pageSize = PAGE_SIZE, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;

  const clamp = (p) => Math.min(Math.max(1, p), totalPages);

  // Windowed page numbers around the current page, capped at 7 slots.
  const pages = [];
  const windowSize = 2;
  const start = Math.max(1, page - windowSize);
  const end = Math.min(totalPages, page + windowSize);
  for (let p = start; p <= end; p++) pages.push(p);

  return (
    <nav style={styles.wrap} aria-label="Pagination">
      <button
        type="button"
        onClick={() => onPageChange(clamp(page - 1))}
        disabled={page <= 1}
        style={{ ...styles.navBtn, ...(page <= 1 ? styles.navBtnDisabled : {}) }}
        aria-label="Previous page"
      >
        <ChevronLeft size={14} strokeWidth={2} />
      </button>

      {start > 1 && (
        <>
          <button type="button" onClick={() => onPageChange(1)} style={styles.pageBtn}>1</button>
          {start > 2 && <span style={styles.ellipsis}>…</span>}
        </>
      )}

      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPageChange(p)}
          style={{ ...styles.pageBtn, ...(p === page ? styles.pageBtnActive : {}) }}
          aria-current={p === page ? "page" : undefined}
        >
          {p}
        </button>
      ))}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span style={styles.ellipsis}>…</span>}
          <button type="button" onClick={() => onPageChange(totalPages)} style={styles.pageBtn}>
            {totalPages}
          </button>
        </>
      )}

      <button
        type="button"
        onClick={() => onPageChange(clamp(page + 1))}
        disabled={page >= totalPages}
        style={{ ...styles.navBtn, ...(page >= totalPages ? styles.navBtnDisabled : {}) }}
        aria-label="Next page"
      >
        <ChevronRight size={14} strokeWidth={2} />
      </button>
    </nav>
  );
}

const styles = {
  wrap: {
    display: "flex", alignItems: "center", gap: 6, justifyContent: "center",
    padding: "18px 0 4px 0", fontFamily: SANS,
  },
  navBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 30, height: 30, background: COLORS.card, color: COLORS.ink,
    border: `1px solid ${COLORS.border}`, borderRadius: 6, cursor: "pointer", fontFamily: SANS,
  },
  navBtnDisabled: { color: COLORS.inkMuted, cursor: "not-allowed", opacity: 0.5 },
  pageBtn: {
    minWidth: 30, height: 30, padding: "0 8px",
    background: COLORS.card, color: COLORS.ink,
    border: `1px solid ${COLORS.border}`, borderRadius: 6,
    fontSize: 13, fontFamily: SANS, cursor: "pointer",
  },
  pageBtnActive: {
    background: COLORS.dark, color: "#fff", borderColor: COLORS.dark, fontWeight: 600,
  },
  ellipsis: { color: COLORS.inkMuted, padding: "0 2px", fontSize: 13 },
};
